import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { authOperationsFeed } from '../authData';
import type { AuthVisualVariant } from '../authVisualVariant';

interface AuthOperationsFeedProps {
  variant?: AuthVisualVariant;
  compact?: boolean;
}

export const AuthOperationsFeed: React.FC<AuthOperationsFeedProps> = ({
  variant = 'light',
  compact = false,
}) => {
  const dark = variant === 'dark';
  const reduceMotion = useReducedMotion();
  const [offset, setOffset] = useState(0);

  const feedCount = compact ? 2 : 3;
  const visibleFeed = Array.from({ length: feedCount }, (_, i) =>
    authOperationsFeed[(offset + i) % authOperationsFeed.length]
  ).filter((item): item is (typeof authOperationsFeed)[number] => item != null);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const feedTimer = setInterval(
      () => setOffset((i) => (i + 1) % authOperationsFeed.length),
      3200
    );
    return () => clearInterval(feedTimer);
  }, [reduceMotion]);

  return (
    <div className={cn(compact ? 'px-4 py-2' : 'px-5 py-3.5', dark ? 'bg-white/[0.02]' : 'bg-slate-50/60')}>
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        <TrendingUp className="h-3.5 w-3.5" aria-hidden />
        Operations feed
      </p>
      <ul className="space-y-1.5">
        <AnimatePresence mode="popLayout">
          {visibleFeed.map((item) => (
            <motion.li
              key={`${item.id}-${offset}`}
              layout
              initial={reduceMotion ? false : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              {...(reduceMotion ? {} : { exit: { opacity: 0, x: 8 } })}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-between gap-2 rounded-lg px-2 py-1 text-xs"
            >
              <span className={cn('truncate font-medium', dark ? 'text-slate-200' : 'text-slate-700')}>
                {item.action}
              </span>
              <span className={cn('shrink-0', dark ? 'text-slate-500' : 'text-slate-400')}>{item.time}</span>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
    </div>
  );
};
